import { useEffect, useRef, useState } from 'react';
import useHttpTriggerAbsenceCompute from './useHttpTriggerAbsenceCompute';

const COMPUTE_PENDING_KEY = 'absence-report-compute-pending';
const POLL_INTERVAL_MS = 4000;

interface DataAvailability {
    dataAvailable: boolean;
}

interface FetchHandlers<T> {
    onSuccess: (res: { body: T }) => void;
    onError: () => void;
    onFinally: () => void;
}

interface FetchResult {
    exec: () => void;
    abort: () => void;
}

const useAbsenceReportPolling = <T extends DataAvailability>(fetchResource: (handlers: FetchHandlers<T>) => FetchResult, initialData: T) => {
    const { trigger: triggerCompute } = useHttpTriggerAbsenceCompute();
    const [data, setData] = useState<T>(initialData);
    const [isReady, setReady] = useState(false);
    const [isError, setError] = useState(false);
    const abortRef = useRef<(() => void) | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        let unmounted = false;

        const stopPolling = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const fetchData = (onResult: (body: T) => void) => {
            const { exec, abort } = fetchResource({
                onSuccess: res => {
                    setData(res.body);
                    setError(false);
                    onResult(res.body);
                },
                onError: () => setError(true),
                onFinally: () => setReady(true),
            });
            abortRef.current = abort;
            exec();
        };

        const startPolling = () => {
            if (unmounted) return;
            stopPolling();
            intervalRef.current = setInterval(() => {
                fetchData(body => {
                    if (body.dataAvailable) {
                        stopPolling();
                        sessionStorage.removeItem(COMPUTE_PENDING_KEY);
                    }
                });
            }, POLL_INTERVAL_MS);
        };

        fetchData(body => {
            if (body.dataAvailable) {
                sessionStorage.removeItem(COMPUTE_PENDING_KEY);
                return;
            }

            if (sessionStorage.getItem(COMPUTE_PENDING_KEY) === 'true') {
                startPolling();
                return;
            }

            sessionStorage.setItem(COMPUTE_PENDING_KEY, 'true');
            triggerCompute(
                () => {
                    if (!unmounted) startPolling();
                },
                () => {
                    sessionStorage.removeItem(COMPUTE_PENDING_KEY);
                    if (!unmounted) setError(true);
                }
            );
        });

        return () => {
            unmounted = true;
            stopPolling();
            abortRef.current?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { data, isReady, isError };
};

export default useAbsenceReportPolling;
